// ABOUTME: Generic filter dropdown component for the country configurations feature.
// ABOUTME: Supports filter mode (clearable, has "all" state) and selector mode (always has a value).

import {Button, DropdownItems, DropdownListItem, Icon} from '@hm/fundamental'
import {mdiChevronDown, mdiClose} from '@mdi/js'
import React, {useEffect, useRef, useState} from 'react'

export interface FilterDropdownOption {
  value: string
  label: string
  icon?: string
  iconClassName?: string
}

export interface FilterDropdownProps {
  /** The currently selected value */
  value: string
  /** Callback when selection changes */
  onChange: (value: string) => void
  /** Available options */
  options: FilterDropdownOption[]
  /** Label text shown as placeholder (when nothing selected) and as floating label (when value selected). When omitted, no floating label is shown and the height is compact. */
  label?: string
  /** Sentinel value representing "no selection" / "all". When provided, enables clear button. When omitted → always has a value (selector mode). */
  allValue?: string
  /** Size variant */
  size?: 'small' | 'regular' | 'medium' | 'large'
  /** When true, the control is non-interactive and styled as disabled. */
  disabled?: boolean
  /** Additional CSS classes */
  className?: string
  /** Cypress test selector */
  'data-cy'?: string
  /** Accessible name for the trigger. Defaults to `label` when omitted. */
  'aria-label'?: string
  /** IDs of elements describing the control (e.g. error text). */
  'aria-describedby'?: string
  /** Text shown when no option is selected and no label is provided (e.g. "–"). Does not appear in the dropdown list. */
  placeholder?: string
  /** Open the option list above the trigger, for controls at the bottom of a scroll container. */
  openUpwards?: boolean
}

const SIZE_CLASS: Record<string, string> = {
  small: 'w-[170px]',
  regular: 'w-[240px]',
  medium: 'w-full',
  large: 'w-[400px]',
}

export const FilterDropdown = ({
  value,
  onChange,
  options,
  label,
  allValue,
  size = 'regular',
  disabled = false,
  className = '',
  'data-cy': dataCy,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  placeholder,
  openUpwards = false,
}: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const isFilterMode = allValue !== undefined
  const hasSelection = isFilterMode ? value !== allValue : true
  const selectedOption = hasSelection ? options.find((o) => o.value === value) : null
  const isClearable = isFilterMode && hasSelection && !disabled

  const handleSelect = (optionValue: string) => {
    if (isFilterMode && value === optionValue) {
      onChange(allValue)
    } else if (!isFilterMode && value === optionValue) {
    } else {
      onChange(optionValue)
    }
    setIsOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isFilterMode) {
      onChange(allValue)
    }
  }

  const toggleOpen = () => {
    if (!disabled) setIsOpen((o) => !o)
  }

  return (
    <div ref={ref} className={`relative ${SIZE_CLASS[size]} ${className}`} data-cy={dataCy}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={toggleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggleOpen()
          }
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-disabled={disabled}
        aria-label={ariaLabel ?? label}
        aria-describedby={ariaDescribedBy}
        className={`flex items-center ${label ? 'h-[48px]' : 'h-10'} px-1 rounded bg-container_background outline outline-1 outline-offset-[-1px] outline-border_highlight_10 w-full ${disabled ? 'opacity-50 cursor-default pointer-events-none' : 'cursor-pointer'}`}
      >
        <div className="flex-1 px-2 relative flex items-center overflow-hidden h-full">
          {label && selectedOption && (
            <span className="absolute top-1 left-2 text-xs leading-none text-foreground_faded">
              {label}
            </span>
          )}
          <div className={`flex items-center gap-1 ${label && selectedOption ? 'mt-2' : ''}`}>
            {selectedOption ? (
              <>
                {selectedOption.icon && (
                  <Icon path={selectedOption.icon} size="regular" className={selectedOption.iconClassName} />
                )}
                <span className="text-base text-foreground truncate">{selectedOption.label}</span>
              </>
            ) : label ? (
              <span className="text-base font-medium text-foreground_faded">{label}</span>
            ) : placeholder ? (
              <span className="text-base text-foreground_faded">{placeholder}</span>
            ) : null}
          </div>
        </div>

        <div className="pr-3 self-stretch flex items-center shrink-0">
          <Icon path={mdiChevronDown} size="regular" className="text-foreground" />
          {isClearable && (
            <>
              <div className="w-px h-full bg-border_highlight_10 mx-3" />
              <Button
                variant="tertiary"
                type="button"
                onClick={handleClear}
                aria-label="Clear filter"
                icon={mdiClose}
                size="extraSmall"
                className="!h-auto !p-0 !rounded-none !text-foreground hover:!bg-transparent active:!bg-transparent"
              />
            </>
          )}
        </div>
      </div>

      <DropdownItems
        isOpen={isOpen && !disabled}
        className={`${openUpwards ? 'bottom-full mb-1' : 'top-full mt-1'} left-0 !w-full !max-w-none`}
      >
        {options.map((opt, idx) => (
          <DropdownListItem
            key={opt.value}
            title={opt.label}
            icon={opt.icon}
            iconClassName={opt.iconClassName}
            onClick={() => handleSelect(opt.value)}
            isSelected={value === opt.value}
            className={
              idx < options.length - 1
                ? 'relative !border-b-0 after:absolute after:bottom-0 after:left-4 after:right-4 after:h-px after:bg-border'
                : '!border-b-0'
            }
          />
        ))}
      </DropdownItems>
    </div>
  )
}
